import { useQuery } from '@tanstack/react-query'
import { Link } from 'react-router-dom'
import { apiClient } from '@/api/client'
import { useAuth } from '@/auth/AuthContext'
import { useCollections } from '@/hooks/useCollections'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { Progress } from '@/components/ui/progress'
import { flaggedStudyKind, groupFlaggedWork, relativeDate } from '@/lib/taskRules'
import {
  EmptyNote,
  Figure,
  RowLink,
  RowSkeleton,
  Section,
  StatTile,
  TileGrid,
} from './shared'
import type { IngestPendingReviewItem, StudyRow, StudyState } from '@/api/types'
import type { useTaskBuckets } from './useTaskBuckets'

type Buckets = ReturnType<typeof useTaskBuckets>

const RECENT_INGEST_LIMIT = 6

/**
 * Maintainer home. Four sections, in the order a maintainer needs them:
 * what's blocked on me, how the pipeline is flowing, whether collections are
 * hitting their targets, and what just landed.
 *
 * Every query is gated on the permission its endpoint requires
 * (`study:read_all` for `GET /studies`, `task:review` for
 * `GET /ingest/needs-review`), because a 403 on a poll raises `App.tsx`'s
 * global destructive toast. Admin holds both, so the same component serves
 * admin — `AdminHome` composes this and adds its own sections.
 */
export function MaintainerHome({ buckets }: { buckets: Buckets }) {
  const { can } = useAuth()
  const canReadStudies = can('study:read_all')
  const canReview = can('task:review')

  // Same canonical keys `/work` and the notification bell use, so this is a
  // shared cache entry rather than a third poll of the same payload.
  const flaggedStudies = useQuery({
    queryKey: ['studies', 'flagged'],
    queryFn: () => apiClient.listStudies({ flagged: true }, 100, 0),
    enabled: canReadStudies,
  })

  const qcHolds = useQuery<IngestPendingReviewItem[]>({
    queryKey: ['ingest', 'needs-review'],
    queryFn: () => apiClient.listIngestNeedsReview(),
    enabled: canReview,
  })

  return (
    <>
      <NeedsYouNow
        buckets={buckets}
        qcItems={qcHolds.data ?? []}
        flaggedStudies={flaggedStudies.data?.items ?? []}
        loading={buckets.isLoading || flaggedStudies.isLoading || qcHolds.isLoading}
      />
      <PipelineFunnel enabled={canReadStudies} />
      <CollectionTargets />
      <RecentIngest enabled={canReadStudies} />
    </>
  )
}

function NeedsYouNow({
  buckets,
  qcItems,
  flaggedStudies,
  loading,
}: {
  buckets: Buckets
  qcItems: IngestPendingReviewItem[]
  flaggedStudies: StudyRow[]
  loading: boolean
}) {
  // Same split `FlaggedDecisions` makes — through the same helper, so these tiles
  // count the cards on `/work?tab=decide` rather than the rows behind them. Two
  // bugs lived in the row count: a 2-slot read node is two rows and one card, so a
  // single disagreement read as two; and every flagged row landed on the
  // "Escalated masks" tile, which filed reader disagreements under a heading about
  // the mask rework limit — the mask has no escalation path left (`on_limit` is
  // `retry` now), so that tile was naming the wrong work entirely.
  const taskGroups = groupFlaggedWork(buckets.flagged)
  const maskEscalations = taskGroups.filter((g) => g.kind === 'mask_escalation')
  const taskTiebreaks = taskGroups.filter((g) => g.kind === 'tiebreak')
  // A study whose flag already has a task row is counted once, on the task side.
  const flaggedTaskStudies = new Set(taskGroups.map((g) => g.studyUid))
  const studyLevel = flaggedStudies.filter((s) => !flaggedTaskStudies.has(s.study_uid))
  const segFailures = studyLevel.filter((s) => flaggedStudyKind(s) === 'seg_failure')
  const disagreements = studyLevel.filter(
    (s) => flaggedStudyKind(s) === 'anomaly_disagreement',
  )
  const readerDisagreements = disagreements.length + taskTiebreaks.length
  const total =
    qcItems.length + segFailures.length + readerDisagreements + maskEscalations.length

  return (
    <Section
      title="Needs you now"
      description="Work that is stopped until a maintainer decides. Nothing else on this page is blocking."
      action={
        <Button asChild variant="outline" size="sm">
          <Link to="/work?tab=decide">Open needs decision</Link>
        </Button>
      }
    >
      {!loading && total === 0 ? (
        <EmptyNote>
          Nothing is blocked on you. Ingestion QC holds, auto-segmentation failures, escalated
          masks and reader disagreements all land here — an empty list means the pipeline is
          running itself.
        </EmptyNote>
      ) : (
        <TileGrid>
          <StatTile
            to={
              qcItems.length === 1
                ? `/work?tab=decide&study=${encodeURIComponent(qcItems[0].study_uid)}`
                : '/work?tab=decide'
            }
            label="Ingestion QC holds"
            value={qcItems.length}
            hint="Paused mid-ingest. The study has no tasks at all until this clears."
            emptyHint="No QC holds — every ingest passed."
            tone="attention"
            loading={loading}
          />
          <StatTile
            to="/work?tab=decide"
            label="Auto-seg failures"
            value={segFailures.length}
            hint="Retry after fixing model routing, proceed with what exists, or quarantine."
            emptyHint="No auto-segmentation failures."
            tone="attention"
            loading={loading}
          />
          <StatTile
            to="/work?tab=decide"
            label="Escalated masks"
            value={maskEscalations.length}
            hint="A mask was disapproved to the rework limit, so the verdict became yours."
            emptyHint="No escalations — reviews are resolving inside the rework limit."
            tone="attention"
            loading={loading}
          />
          <StatTile
            to="/work?tab=decide"
            label="Reader disagreements"
            value={readerDisagreements}
            hint="Two independent reads disagreed. A radiologist tiebreak or your call closes it."
            emptyHint="No disagreements between readers."
            tone="attention"
            loading={loading}
          />
          <StatTile
            to="/work?tab=decide"
            label="Verdicts I can give"
            value={buckets.decidable.length}
            hint="Ordinary approve/rework decisions on submitted work."
            emptyHint="No submissions waiting on a verdict."
            loading={loading}
          />
        </TileGrid>
      )}
    </Section>
  )
}

/** `GET /studies?study_state=…&limit=1` — read `total`, discard the row. Cheaper
 *  and exact, where a client-side tally over a 200-row page would be a sample. */
function useStateTotal(state: StudyState, enabled: boolean) {
  return useQuery({
    queryKey: ['studies', 'list', { study_state: state }, 1, 0],
    queryFn: () => apiClient.listStudies({ study_state: state }, 1, 0),
    enabled,
  })
}

function PipelineFunnel({ enabled }: { enabled: boolean }) {
  const registered = useStateTotal('registered', enabled)
  const inProgress = useStateTotal('in_progress', enabled)
  const complete = useStateTotal('complete', enabled)
  const loading =
    enabled && (registered.isLoading || inProgress.isLoading || complete.isLoading)

  const counts = [
    {
      label: 'Registered',
      value: registered.data?.total ?? 0,
      hint: 'Ingested and queryable, not started. Nothing annotation-related runs until someone starts them.',
    },
    {
      label: 'In progress',
      value: inProgress.data?.total ?? 0,
      hint: 'A StudyWorkflow is running: auto-seg, editing, review or an anomaly read.',
    },
    {
      label: 'Complete',
      value: complete.data?.total ?? 0,
      hint: 'Every applicable track finalized.',
    },
  ]

  return (
    <Section
      title="Pipeline"
      description="Every started study, by lifecycle state. Exact totals from GET /studies, not a sample."
    >
      <Card>
        <CardContent className="grid gap-4 p-4 sm:grid-cols-3">
          {counts.map((c) => (
            <Figure key={c.label} label={c.label} value={c.value} hint={c.hint} loading={loading} />
          ))}
        </CardContent>
      </Card>
      {/* ponytail: figures, not links — no route filters studies by study_state
          without a collection id, so a link here would silently drop the filter
          and land on the whole list. Per-collection state filters do exist:
          /collections/:id?tab=studies&state=… */}
      <p className="text-xs text-muted-foreground">
        These three aren't links: study state is filterable per collection, not globally. Open a
        collection below to see its own funnel.
      </p>
    </Section>
  )
}

function CollectionTargets() {
  const { data, isLoading } = useCollections()
  // A collection with no target has nothing to be behind on — sort the ones that
  // do to the front, furthest from target first.
  const withTarget = (data ?? [])
    .filter((c) => (c.target_study_count ?? 0) > 0)
    .sort(
      (a, b) =>
        a.study_count / (a.target_study_count ?? 1) -
        b.study_count / (b.target_study_count ?? 1),
    )
    .slice(0, 5)

  return (
    <Section
      title="Collections against target"
      description="How full each collection is against the target you set."
      action={
        <Button asChild variant="outline" size="sm">
          <Link to="/collections">All collections</Link>
        </Button>
      }
    >
      {isLoading ? (
        <RowSkeleton rows={2} />
      ) : withTarget.length === 0 ? (
        <EmptyNote>
          No collection has a target study count yet. Set one in a collection's Settings tab and
          its progress appears here — it's the only way this page can tell "small" from "behind".
        </EmptyNote>
      ) : (
        <div className="space-y-2">
          {withTarget.map((c) => {
            const target = c.target_study_count ?? 0
            const pct = target ? Math.min(100, Math.round((c.study_count / target) * 100)) : 0
            return (
              <Link
                key={c.id}
                to={`/collections/${c.id}`}
                className="block rounded-md border bg-card p-3 transition-colors hover:bg-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              >
                <div className="flex items-baseline justify-between gap-3">
                  <span className="truncate text-sm font-medium">{c.name}</span>
                  <span className="shrink-0 font-mono text-xs tabular-nums text-muted-foreground">
                    {c.study_count} / {target}
                  </span>
                </div>
                <Progress
                  value={pct}
                  className="mt-2 h-1.5"
                  aria-label={`${c.name}: ${c.study_count} of ${target} studies`}
                />
                <p className="mt-1 text-xs text-muted-foreground">
                  <span className="font-mono">{c.registered_study_count}</span> still registered ·{' '}
                  <span className="font-mono">{c.started_study_count}</span> started
                  {c.auto_start ? ' · auto-start on' : ''}
                </p>
              </Link>
            )
          })}
        </div>
      )}
    </Section>
  )
}

function RecentIngest({ enabled }: { enabled: boolean }) {
  // `GET /studies` orders by `updated_at DESC NULLS LAST`, so the first page is
  // the recent-activity list — no sort param needed.
  const { data, isLoading } = useQuery({
    queryKey: ['studies', 'list', {}, RECENT_INGEST_LIMIT, 0],
    queryFn: () => apiClient.listStudies({}, RECENT_INGEST_LIMIT, 0),
    enabled,
  })

  return (
    <Section
      title="Recent ingest"
      description="The most recently updated studies. Newly ingested studies land at the top."
      action={
        <Button asChild variant="outline" size="sm">
          <Link to="/upload">Upload studies</Link>
        </Button>
      }
    >
      {isLoading ? (
        <RowSkeleton rows={3} />
      ) : (data?.items.length ?? 0) === 0 ? (
        <EmptyNote>
          No studies yet. Ingest is the only way in — Orthanc's DIMSE listener is off, so a study
          arrives through{' '}
          <Link to="/upload" className="underline underline-offset-2">
            Upload
          </Link>{' '}
          or the SDK.
        </EmptyNote>
      ) : (
        <div className="space-y-2">
          {(data?.items ?? []).map((s) => (
            <RowLink
              key={s.study_uid}
              to={`/data?study_uid=${encodeURIComponent(s.study_uid)}`}
              primary={<span className="font-mono">{s.study_uid}</span>}
              secondary={
                <>
                  {s.modalities.join(', ') || 'No modality'} ·{' '}
                  {s.body_parts.join(', ') || 'No region'} · {s.study_state.replace(/_/g, ' ')}
                </>
              }
              right={s.updated_at ? relativeDate(s.updated_at) : undefined}
            />
          ))}
        </div>
      )}
    </Section>
  )
}
